import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface SlotBookingOption {
  minutes: number;
  slotCount: number;
  slotIndex: number;
}

interface ChargerInfoData {
  code?: string;
  status: 'available' | 'reserved' | 'out_of_order';
  availableUntil: string | null;
  availableFrom: string | null;
  slotBookingOptions: SlotBookingOption[] | null;
  uuid?: string;
}

function ChargerInfo() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [chargerInfo, setChargerInfo] = useState<ChargerInfoData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bookingLoading, setBookingLoading] = useState<number | null>(null); // tracks slotIndex
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchChargerInfo = useCallback(async () => {
    if (!uuid) {
      navigate('/invalid');
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError(null);

    try {
      const rootUrl = process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_BACKEND_URL
        : window.location.origin;

      // Validate QR code and get charger info using GET request with path parameter
      const validateResponse = await fetch(`${rootUrl}/qr-code/validate/${encodeURIComponent(uuid)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
      });

      if (!validateResponse.ok) {
        navigate('/invalid');
        return;
      }

      const data = await validateResponse.json();
      if (data.code !== 'SUCCESS') {
        navigate('/invalid');
        return;
      }

      setChargerInfo(data);
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Error fetching charger info:', err);
      setError('An error occurred while loading charger information');
      navigate('/invalid');
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [uuid, navigate]);

  useEffect(() => {
    if (!uuid) {
      navigate('/invalid');
      return;
    }

    fetchChargerInfo();

    // Cleanup: abort request on unmount or when uuid changes
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [uuid, navigate, fetchChargerInfo]);

  const handleReserve = async (slotCount: number, slotIndex: number, minutes: number) => {
    if (!chargerInfo || !uuid) return;

    setBookingLoading(slotIndex);

    try {
      const rootUrl = process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_BACKEND_URL
        : window.location.origin;

      const response = await fetch(`${rootUrl}/qr-code/booking/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uuid, slotCount, slotIndex })
      });

      if (response.ok) {
        // Refresh charger info after booking
        await fetchChargerInfo();
        alert(`Successfully reserved charger for ${minutes} minutes!`);
      } else {
        const data = await response.json();
        alert(`Failed to create booking: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error creating booking:', err);
      alert('An error occurred while creating the booking');
    } finally {
      setBookingLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'reserved':
        return 'warning';
      case 'out_of_order':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'reserved':
        return 'Reserved';
      case 'out_of_order':
        return 'Out of Order';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error || !chargerInfo) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Error
          </Typography>
          <Typography variant="body1" color="error">
            {error || 'Charger information not available'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        py: { xs: 2, sm: 4 },
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ mb: 4, textAlign: 'center', fontWeight: 'bold' }}
        >
          Charger Information
        </Typography>

        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            backgroundColor: '#1e1e1e',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Status</Typography>
            <Chip
              label={getStatusLabel(chargerInfo.status)}
              color={getStatusColor(chargerInfo.status) as any}
              size="medium"
            />
          </Box>

          {/* {chargerInfo.availableFrom && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Available From
              </Typography>
              <Typography variant="body1">
                {format(new Date(chargerInfo.availableFrom), 'PPpp')}
              </Typography>
            </Box>
          )} */}
          {chargerInfo.availableUntil && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Available Until
              </Typography>
              <Typography variant="body1">
                {format(new Date(chargerInfo.availableUntil), 'PPpp')}
              </Typography>
            </Box>
          )}
        </Paper>

        {chargerInfo.status === 'available' &&
          chargerInfo.slotBookingOptions &&
          Array.isArray(chargerInfo.slotBookingOptions) &&
          chargerInfo.slotBookingOptions.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mt: 'auto',
              }}
            >
              <Typography variant="h6" sx={{ textAlign: 'center', mb: 1 }}>
                Reserve Charger now for
              </Typography>
              {chargerInfo.slotBookingOptions.map((option, index) => (
                <Button
                  key={`${option.minutes}-${option.slotIndex}-${index}`}
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => handleReserve(option.slotCount, option.slotIndex, option.minutes)}
                  disabled={bookingLoading !== null}
                  sx={{
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                  }}
                >
                  {bookingLoading === option.slotIndex ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    `${option.minutes} minutes`
                  )}
                </Button>
              ))}
            </Box>
          )}

        {chargerInfo.status === 'reserved' && (
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mt: 2,
              backgroundColor: '#1e1e1e',
              textAlign: 'center',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              This charger is currently reserved. Please check back later.
            </Typography>
          </Paper>
        )}

        {chargerInfo.status === 'out_of_order' && (
          <Paper
            elevation={3}
            sx={{
              p: 2,
              mt: 2,
              backgroundColor: '#1e1e1e',
              textAlign: 'center',
            }}
          >
            <Typography variant="body1" color="error">
              This charger is currently out of order. Please use another charger.
            </Typography>
          </Paper>
        )}
      </Box>
    </Container>
  );
}

export default ChargerInfo;
